<template>
  <div class="initial-uploader">
    <div class="upload-left">
      <el-upload
        ref="upload"
        class="drop-upload"
        :class="{ 'has-images': uploadedImages.length, 'has-many-images': uploadedImages.length > 4 }"
        action=""
        drag
        multiple
        accept="image/jpeg,image/jpg,image/png"
        :show-file-list="false"
        :http-request="customUpload"
        :disabled="parseLoading"
        @change.native.capture="handleUploadSelection"
        @drop.native.capture="handleUploadSelection"
      >
        <div class="drop-empty" :class="{ 'has-images': uploadedImages.length }">
          <div v-if="uploadedImages.length" class="uploaded-strip" :class="{ 'is-many': uploadedImages.length > 4 }">
            <div v-for="item in visibleImages" :key="item.clientImageId" class="uploaded-thumb">
              <img :src="getPreview(item)" alt="">
              <button type="button" :disabled="parseLoading" @click.stop="removeImage(item)">×</button>
            </div>
            <button v-if="hiddenImageCount > 0" type="button" class="uploaded-more" @click.prevent.stop="openFileDialog">
              +{{ hiddenImageCount }}
            </button>
          </div>
          <div v-else class="upload-illustration">
            <span class="cloud cloud-left"></span>
            <span class="cloud cloud-right"></span>
            <span class="upload-box"><i></i></span>
          </div>
          <h2>点击或拖拽图片到此处上传</h2>
          <p>上传多张图片将生成多个零件</p>
          <span class="upload-rule"><i>i</i> 单张图片≤10M，单次最多20张，建议尺寸：1000×1000以内的正方形，支持 jpeg / jpg / png 格式</span>
          <button class="pick-button" type="button" @click.stop="openFileDialog"><em></em>选择图片</button>
        </div>
      </el-upload>

      <el-button type="primary" class="parse-button" :loading="parseLoading || uploadLoading" :disabled="!canParseImages" @click="parseImages">
        <span class="parse-icon">⌁</span> 上传图片解析零件信息
      </el-button>
    </div>

    <aside class="upload-assist-panel">
      <section class="upload-ai-card">
        <h2>AI可帮你完成</h2>
        <article>
          <span class="assist-icon ai">AI</span>
          <div><h3>自动识别零件信息</h3><p>识别名称、工艺、材质等关键信息</p></div>
        </article>
        <article>
          <span class="assist-icon stack">≋</span>
          <div><h3>批量生成零件草稿</h3><p>多张图片可自动生成多个零件</p></div>
        </article>
        <article>
          <span class="assist-icon check">✓</span>
          <div><h3>发布前可校对编辑</h3><p>解析完成后可人工核对再发布</p></div>
        </article>
      </section>
      <section class="upload-suggest-card">
        <h2><span class="suggest-icon"></span>上传建议</h2>
        <ul>
          <li>优先上传主体清晰的零件图片</li>
          <li>避免反光、遮挡、背景过杂</li>
          <li>支持 jpeg / jpg / png</li>
        </ul>
      </section>
    </aside>

    <transition name="analysis-fade">
      <div v-if="parseLoading" class="analysis-mask">
        <div class="analysis-modal">
          <div class="analysis-orbit">
            <span class="orbit-dot dot-a"></span>
            <span class="orbit-dot dot-b"></span>
            <span class="orbit-dot dot-c"></span>
            <strong>AI</strong>
          </div>
          <h2>AI 正在解析零件信息</h2>
          <p>识别图片中的零件轮廓与特征</p>
          <div class="analysis-progress">
            <i :style="{ width: analysisProgress + '%' }"></i>
          </div>
          <b>{{ analysisProgress }}%</b>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { parsePartsFromImagesProgressive } from '@/utils/api/agent-hub-product-case';
import { uploadSupplierProductImage } from '@/utils/api/product-case';
import { PRODUCT_IMAGE_UPLOAD_SOURCE, SITE_TYPE } from '@/utils/product-case/constants';
import { createImageItem, extractRspData, getImagePreview, normalizePart } from '@/utils/product-case/model';
import { createMockParseResult, createMockUpload } from '@/utils/product-case/mock';

const MIN_ANALYSIS_PROGRESS_LIMIT = 82;
const MAX_ANALYSIS_PROGRESS_LIMIT = 92;
const MAX_PROGRESS_STEP = 5;
const PROGRESS_STEP_DURATION = 3000;
const PROGRESS_FRAME_INTERVAL = 120;
const MAX_INITIAL_UPLOAD_IMAGES = 20;
const PRODUCT_IMAGE_MIME_TYPE = /^image\/(jpeg|jpg|png)$/;

export default {
  name: 'InitialImageUploader',
  props: {
    siteType: {
      type: Number,
      default: SITE_TYPE.CN
    },
    mockEnabled: {
      type: Boolean,
      default: false
    },
    userInfo: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      uploadedImages: [],
      hasUploadFailure: false,
      pendingUploadCount: 0,
      uploadLoading: false,
      uploadLimitMessage: null,
      parseLoading: false,
      analysisProgress: 0,
      analysisProgressLimit: MAX_ANALYSIS_PROGRESS_LIMIT,
      progressTimer: null,
      progressFrameTimer: null
    };
  },
  computed: {
    canParseImages() {
      return this.uploadedImages.length > 0
        && !this.uploadLoading
        && !this.parseLoading
        && !this.hasUploadFailure
        && this.uploadedImages.every(item => typeof item.originalHashCode === 'string' && item.originalHashCode.trim());
    },
    visibleImages() {
      return this.uploadedImages.length > 12 ? this.uploadedImages.slice(0, 11) : this.uploadedImages;
    },
    hiddenImageCount() {
      return Math.max(0, this.uploadedImages.length - this.visibleImages.length);
    }
  },
  beforeDestroy() {
    this.clearProgressTimer();
    if (this.uploadLimitMessage) this.uploadLimitMessage.close();
  },
  methods: {
    handleUploadSelection(event) {
      if (this.parseLoading) return;
      const files = event.type === 'drop'
        ? (event.dataTransfer && event.dataTransfer.files)
        : (event.target && event.target.files);
      if (!files || !files.length) return;
      this.hasUploadFailure = false;
      if (!this.canAppendImage()) return;
      // Element UI filters unsupported drag-and-drop files before customUpload runs.
      if (event.type === 'drop') {
        Array.from(files).filter(file => !PRODUCT_IMAGE_MIME_TYPE.test(file.type)).forEach(file => this.validateFile(file));
      }
    },
    async customUpload({ file }) {
      if (this.parseLoading) return;
      if (!this.isCompanyValidated('您的账号尚未验证，请联系客服经理')) return;
      if (!this.canAppendImage()) return;
      if (!this.validateFile(file)) return;
      this.pendingUploadCount += 1;
      this.uploadLoading = true;
      try {
        let data;
        if (this.mockEnabled) {
          data = createMockUpload(file);
        } else {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('source', PRODUCT_IMAGE_UPLOAD_SOURCE);
          const res = await uploadSupplierProductImage(formData);
          data = extractRspData(res);
        }
        const image = createImageItem({ ...data, originalName: data.originalName || file.name }, this.uploadedImages.length + 1);
        image.hashCode = data.hashCode;
        image.previewUrl = data.previewUrl || image.previewUrl;
        this.uploadedImages.push(image);
        this.emitUploadChange();
      } catch (error) {
        const message = (error && ((error.rspResult && error.rspResult.message) || error.message)) || '图片上传失败，请重新上传';
        this.hasUploadFailure = true;
        this.$message.error(message);
        throw error;
      } finally {
        this.pendingUploadCount = Math.max(0, this.pendingUploadCount - 1);
        this.uploadLoading = this.pendingUploadCount > 0;
      }
    },
    canAppendImage() {
      if (this.uploadedImages.length + this.pendingUploadCount < MAX_INITIAL_UPLOAD_IMAGES) return true;
      if (!this.uploadLimitMessage) {
        this.uploadLimitMessage = this.$message.warning({
          message: `单次最多支持解析${MAX_INITIAL_UPLOAD_IMAGES}张图片，请删除部分图片后再上传`,
          onClose: () => { this.uploadLimitMessage = null; }
        });
      }
      return false;
    },
    validateFile(file) {
      const isImage = PRODUCT_IMAGE_MIME_TYPE.test(file.type);
      if (!isImage) {
        const message = '仅支持 jpeg / jpg / png 格式图片';
        this.hasUploadFailure = true;
        this.$message.warning(message);
        return false;
      }
      if (file.size > 10 * 1024 * 1024) {
        const message = '单张图片不能超过 10M';
        this.hasUploadFailure = true;
        this.$message.warning(message);
        return false;
      }
      return true;
    },
    async openFileDialog() {
      if (this.parseLoading) return;
      if (!(await this.ensureCompanyValidated('您的账号尚未验证，请联系客服经理'))) return;
      this.$nextTick(() => {
        if (this.parseLoading) return;
        const upload = this.$refs.upload;
        const input = upload && upload.$el && upload.$el.querySelector('input[type="file"]');
        if (input) {
          input.click();
          return;
        }
        if (upload && upload.handleClick) upload.handleClick();
      });
    },
    getPreview(image) {
      return getImagePreview(image);
    },
    removeImage(image) {
      if (this.parseLoading) return;
      this.uploadedImages = this.uploadedImages.filter(item => item.clientImageId !== image.clientImageId);
      this.emitUploadChange();
    },
    emitUploadChange() {
      this.$emit('upload-change', this.uploadedImages.length);
    },
    sleep(delay) {
      return new Promise(resolve => setTimeout(resolve, delay));
    },
    clearProgressTimer() {
      if (this.progressTimer) {
        clearTimeout(this.progressTimer);
        this.progressTimer = null;
      }
      if (this.progressFrameTimer) {
        clearInterval(this.progressFrameTimer);
        this.progressFrameTimer = null;
      }
    },
    startAnalysisProgress() {
      this.clearProgressTimer();
      this.analysisProgress = 8;
      this.analysisProgressLimit = this.getRandomProgressLimit();
      this.runRandomProgressStep();
    },
    getRandomProgressLimit() {
      return Math.floor(Math.random() * (MAX_ANALYSIS_PROGRESS_LIMIT - MIN_ANALYSIS_PROGRESS_LIMIT + 1)) + MIN_ANALYSIS_PROGRESS_LIMIT;
    },
    getRandomProgressStep() {
      return Math.floor(Math.random() * MAX_PROGRESS_STEP) + 1;
    },
    runRandomProgressStep() {
      if (this.analysisProgress >= this.analysisProgressLimit) return;
      const startProgress = this.analysisProgress;
      const targetProgress = Math.min(this.analysisProgressLimit, startProgress + this.getRandomProgressStep());
      const startTime = Date.now();

      this.progressFrameTimer = setInterval(() => {
        const progressRatio = Math.min(1, (Date.now() - startTime) / PROGRESS_STEP_DURATION);
        const easedRatio = 1 - Math.pow(1 - progressRatio, 2);
        this.analysisProgress = Math.min(targetProgress, Math.round(startProgress + (targetProgress - startProgress) * easedRatio));
        if (progressRatio < 1) return;
        clearInterval(this.progressFrameTimer);
        this.progressFrameTimer = null;
        this.progressTimer = setTimeout(() => this.runRandomProgressStep(), PROGRESS_FRAME_INTERVAL);
      }, PROGRESS_FRAME_INTERVAL);
    },
    async finishAnalysisProgress() {
      this.clearProgressTimer();
      while (this.analysisProgress < 100) {
        this.analysisProgress = Math.min(100, this.analysisProgress + 8);
        await this.sleep(35);
      }
      await this.sleep(320);
    },
    async parseImages() {
      if (!this.canParseImages) return;
      if (!(await this.ensureCompanyValidated('您的账号尚未验证，请联系客服经理'))) return;
      if (!this.canParseImages) return;
      if (this.uploadedImages.length > MAX_INITIAL_UPLOAD_IMAGES) {
        this.$message.warning(`单次最多支持解析${MAX_INITIAL_UPLOAD_IMAGES}张图片，请删除部分图片后再解析`);
        return;
      }
      this.parseLoading = true;
      this.startAnalysisProgress();
      try {
        let payload;
        if (this.mockEnabled) {
          await this.sleep(1500);
          payload = { parts: createMockParseResult(this.uploadedImages, this.siteType), taskId: '' };
        } else {
          const res = await parsePartsFromImagesProgressive({
            siteType: this.siteType,
            targetLanguage: this.siteType === SITE_TYPE.INTL ? 'en-US' : 'zh-CN',
            regenerateImages: true,
            images: this.uploadedImages.map(item => ({
              clientImageId: item.clientImageId,
              hashCode: item.originalHashCode,
              originalName: item.originalName
            }))
          });
          const data = extractRspData(res);
          payload = {
            taskId: data.taskId || '',
            status: data.status || '',
            parts: (data.parts || []).map(item => normalizePart(item, this.siteType))
          };
        }
        await this.finishAnalysisProgress();
        this.$emit('parsed', payload);
      } finally {
        this.clearProgressTimer();
        this.parseLoading = false;
      }
    },
    async ensureCompanyValidated(message) {
      if (this.userInfo && this.userInfo.isMember !== undefined) {
        return this.isCompanyValidated(message);
      }
      try {
        await this.$store.dispatch('checkUserInfo');
      } catch (error) {
        // 登录信息刷新失败时继续使用当前缓存的用户信息做前端拦截。
      }
      return this.isCompanyValidated(message);
    },
    isCompanyValidated(message) {
      const userInfo = this.userInfo && this.userInfo.isMember !== undefined ? this.userInfo : this.$store.state.userInfo;
      if (Number(userInfo && userInfo.isMember) === 1) return true;
      this.$message.warning(message);
      return false;
    }
  }
};
</script>

<style lang="scss" scoped>
.initial-uploader {
  display: grid;
  grid-template-columns: minmax(620px, 974px) 402px;
  gap: 36px;
  align-items: start;
}
.upload-left {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.drop-upload,
::v-deep .el-upload,
::v-deep .el-upload-dragger {
  width: 100%;
}
.drop-upload {
  margin-top: 0;
}
::v-deep .el-upload-dragger {
  height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #8eb2ff;
  border-radius: 12px;
  background: rgba(255, 255, 255, .72);
}
.drop-upload.has-images ::v-deep .el-upload-dragger {
  align-items: flex-start;
  overflow: visible;
}
.drop-upload.has-many-images ::v-deep .el-upload-dragger {
  height: 666px;
}
.drop-empty {
  width: 100%;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  text-align: center;
  color: #657187;
}
.drop-empty.has-images {
  padding-top: 0;
}
.uploaded-strip {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  max-width: 648px;
  margin: 0 auto 18px;
}
.uploaded-strip.is-many {
  display: grid;
  grid-template-columns: repeat(4, 150px);
  justify-content: center;
}
.uploaded-thumb {
  position: relative;
  width: 150px;
  height: 150px;
  box-sizing: border-box;
  border: 1px solid #e3eaf7;
  border-radius: 8px;
  background: #fff;
  overflow: hidden;
}
.uploaded-thumb:first-child {
  border-color: #7769ff;
  box-shadow: 0 0 0 1px rgba(100, 93, 255, .28);
}
.uploaded-thumb img {
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  object-fit: contain;
  padding: 12px;
}
.uploaded-thumb button {
  position: absolute;
  z-index: 3;
  right: 6px;
  top: 6px;
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(53, 64, 88, .88);
  color: #fff;
  line-height: 1;
  cursor: pointer;
}
.uploaded-more {
  width: 150px;
  height: 150px;
  box-sizing: border-box;
  border: 1px solid #d9e4f3;
  border-radius: 8px;
  background: #fff;
  color: #455778;
  font-size: 20px;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 8px 22px rgba(40, 72, 140, .08);
}
.uploaded-more:hover {
  border-color: #7769ff;
  color: #145dff;
  box-shadow: 0 0 0 2px rgba(100, 93, 255, .18);
}
.upload-illustration {
  position: relative;
  width: 180px;
  height: 118px;
  margin: 0 auto 6px;
}
.upload-box {
  position: absolute;
  left: 50%;
  top: 18px;
  width: 72px;
  height: 82px;
  transform: translateX(-50%);
  border-radius: 16px 16px 20px 20px;
  background: linear-gradient(180deg, #eef4ff 0%, #4c7bff 100%);
  box-shadow: 0 18px 44px rgba(54, 104, 255, .32);
}
.upload-box::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 54px;
  border-radius: 14px;
  background: #edf3ff;
}
.upload-box i {
  position: absolute;
  z-index: 1;
  left: 50%;
  top: 28px;
  width: 0;
  height: 0;
  transform: translateX(-50%);
  border-left: 10px solid transparent;
  border-right: 10px solid transparent;
  border-bottom: 15px solid #527dff;
}
.upload-box i::after {
  content: '';
  position: absolute;
  left: -3px;
  top: 14px;
  width: 6px;
  height: 22px;
  background: #527dff;
}
.cloud {
  position: absolute;
  bottom: 20px;
  width: 42px;
  height: 22px;
  border-radius: 999px;
  background: #e9f0ff;
}
.cloud::before,
.cloud::after {
  content: '';
  position: absolute;
  bottom: 10px;
  border-radius: 50%;
  background: #e9f0ff;
}
.cloud::before { left: 8px; width: 28px; height: 28px; }
.cloud::after { right: -8px; width: 20px; height: 20px; }
.cloud-left { left: 28px; }
.cloud-right { right: 24px; transform: scale(.9); }
.drop-empty h2 {
  margin: 0 0 4px;
  color: #00143d;
  font-size: 24px;
  font-weight: 900;
}
.drop-empty p {
  margin: 0 0 8px;
  color: #586981;
  font-size: 16px;
}
.upload-rule {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  color: #8a99b0;
  font-size: 12px;
}
.upload-rule i {
  width: 16px;
  height: 16px;
  display: inline-grid;
  place-items: center;
  border: 1px solid #b9c9e6;
  border-radius: 50%;
  color: #9badcc;
  font-style: normal;
  font-size: 12px;
}
.pick-button {
  height: 54px;
  min-width: 252px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 12px auto 0;
  border: 0;
  border-radius: 8px;
  background: #f0f4ff;
  color: #145dff;
  font-size: 18px;
  font-weight: 900;
  cursor: pointer;
  transition: color .18s ease, background .18s ease, box-shadow .18s ease;
}
.pick-button:hover {
  background: #e8efff;
  color: #0d55f5;
  box-shadow: 0 10px 24px rgba(43, 101, 255, .14);
}
.pick-button:active {
  transform: translateY(1px);
}
.pick-button em {
  width: 20px;
  height: 18px;
  border: 2px solid #145dff;
  border-radius: 3px;
  position: relative;
}
.pick-button em::after {
  content: '';
  position: absolute;
  right: -5px;
  top: -5px;
  width: 7px;
  height: 7px;
  border-top: 2px solid #145dff;
  border-right: 2px solid #145dff;
}
.parse-button {
  width: 500px;
  height: 64px;
  margin-top: 20px;
  border: 0;
  border-radius: 8px;
  background: linear-gradient(90deg, #2166ff 0%, #7040ef 100%);
  box-shadow: none;
  font-size: 18px;
  font-weight: 900;
  cursor: pointer;
}
.parse-button.is-disabled {
  cursor: not-allowed;
}
.parse-icon {
  margin-right: 14px;
  font-size: 20px;
}
.upload-assist-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.upload-ai-card,
.upload-suggest-card {
  border: 1px solid #dbe5f2;
  border-radius: 12px;
  background: #fff;
  box-shadow: none;
  padding: 24px;
}
h2 {
  margin: 0 0 20px;
  color: #00143d;
  font-size: 18px;
  line-height: 1;
  font-weight: 900;
}
article {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-bottom: 24px;
}
article:last-child { margin-bottom: 0; }
.assist-icon {
  width: 52px;
  height: 52px;
  display: grid;
  place-items: center;
  flex: 0 0 auto;
  border-radius: 10px;
  color: #fff;
  font-size: 24px;
  font-weight: 900;
}
.assist-icon.ai { background: linear-gradient(135deg, #6b8cff, #1f66ff); }
.assist-icon.stack { background: linear-gradient(135deg, #6fe7d6, #26c6aa); }
.assist-icon.check { background: linear-gradient(135deg, #9685ff, #6458ee); }
h3 {
  margin: 0 0 8px;
  color: #00143d;
  font-size: 16px;
  font-weight: 900;
}
p {
  margin: 0;
  color: #61718b;
  font-size: 14px;
}
.upload-suggest-card h2 {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}
.suggest-icon {
  width: 18px;
  height: 24px;
  border: 3px solid #1764ff;
  border-radius: 10px 10px 8px 8px;
  position: relative;
  box-sizing: border-box;
}
.suggest-icon::after {
  content: '';
  position: absolute;
  left: 50%;
  bottom: -8px;
  width: 8px;
  height: 3px;
  border-radius: 2px;
  background: #1764ff;
  transform: translateX(-50%);
}
ul {
  display: grid;
  gap: 15px;
  padding-left: 0;
  margin: 0;
  color: #526582;
  line-height: 1.25;
  font-size: 15px;
  font-weight: 700;
  list-style: none;
}
li {
  position: relative;
  padding-left: 18px;
}
li::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #1764ff;
  transform: translateY(-50%);
}
.analysis-mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(20, 30, 48, .38);
  backdrop-filter: blur(9px);
}
.analysis-modal {
  width: 440px;
  min-height: 350px;
  padding: 34px 34px 30px;
  border-radius: 10px;
  background: #fff;
  text-align: center;
  box-shadow: 0 28px 72px rgba(14, 26, 52, .28);
}
.analysis-orbit {
  position: relative;
  width: 126px;
  height: 126px;
  margin: 0 auto 26px;
  display: grid;
  place-items: center;
  border: 1px solid #dce6ff;
  border-radius: 50%;
}
.analysis-orbit::before,
.analysis-orbit::after {
  content: '';
  position: absolute;
  border: 1px solid #e7edff;
  border-radius: 50%;
}
.analysis-orbit::before {
  inset: 13px;
}
.analysis-orbit::after {
  inset: 28px;
}
.analysis-orbit strong {
  position: relative;
  z-index: 2;
  width: 72px;
  height: 72px;
  display: grid;
  place-items: center;
  border-radius: 18px;
  background: linear-gradient(135deg, #815dff 0%, #2168ff 55%, #37c8c5 100%);
  color: #fff;
  font-size: 28px;
  font-weight: 900;
  box-shadow: 0 15px 34px rgba(50, 95, 255, .36);
}
.orbit-dot {
  position: absolute;
  z-index: 3;
  width: 9px;
  height: 9px;
  border-radius: 50%;
}
.dot-a { top: 30px; right: 28px; background: #2fd5c6; }
.dot-b { left: 27px; top: 70px; background: #35d2c4; }
.dot-c { left: 58px; bottom: 14px; background: #695dff; }
.analysis-modal h2 {
  margin: 0 0 12px;
  color: #151d2c;
  font-size: 24px;
  line-height: 1;
  font-weight: 900;
}
.analysis-modal p {
  margin: 0 0 22px;
  color: #62718a;
  font-size: 15px;
  font-weight: 700;
}
.analysis-progress {
  height: 10px;
  margin: 0 0 16px;
  border-radius: 999px;
  background: #eaf0f8;
  overflow: hidden;
}
.analysis-progress i {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #2466ff 0%, #35d2c4 100%);
  transition: width .18s ease;
}
.analysis-modal b {
  color: #315dff;
  font-size: 30px;
  line-height: 1;
  font-weight: 900;
}
.analysis-fade-enter-active,
.analysis-fade-leave-active {
  transition: opacity .2s ease;
}
.analysis-fade-enter,
.analysis-fade-leave-to {
  opacity: 0;
}
@media (max-width: 1360px) {
  .initial-uploader { grid-template-columns: 1fr; }
  .upload-assist-panel { grid-template-columns: repeat(2, 1fr); display: grid; }
}
</style>

